import { useQuery } from '@apollo/client'
import { useRef } from 'react'
import { type FragmentOf, graphql } from '~publish/graphql'

import { sanitizeNullableArray } from '~publish/helpers/typeGuards'

export const ChannelNavChannelFragment = graphql(`
  fragment ChannelNavChannelFragment on Channel @_unmask {
    id
    name
    service
    type
    avatar
    timezone
    serverUrl
    locationData {
      location
    }
    isLocked
    isDisconnected
    isQueuePaused
    apiVersion
  }
`)

export const GetSidebarInfo = graphql(
  `
    query GetSidebarInfo {
      # TODO: channels should be a top-level query
      account {
        id
        currentOrganization {
          id
          channels(product: publish) {
            id
            name
            service
            avatar
            type
            ...ChannelNavChannelFragment
          }
        }
      }
    }
  `,
  [ChannelNavChannelFragment],
)

export type ChannelNavChannel = FragmentOf<typeof ChannelNavChannelFragment>

const FIVE_SECONDS = 5 * 1_000

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useChannels() {
  const lastFetchTime = useRef(0)
  const { data, loading, ...rest } = useQuery(GetSidebarInfo, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy(_, { reason, initialFetchPolicy }) {
      // When variables change, the default behavior is to reset
      // options.fetchPolicy to context.initialFetchPolicy.
      if (reason === 'variables-changed') {
        return initialFetchPolicy
      }

      // Force fetching from cache if the last fetch was less than 5 seconds ago
      if (Date.now() - lastFetchTime.current < FIVE_SECONDS) {
        lastFetchTime.current = Date.now()
        return 'cache-only'
      }

      return initialFetchPolicy
    },
  })

  lastFetchTime.current = Date.now()

  const channels = sanitizeNullableArray(
    data?.account?.currentOrganization?.channels,
  )

  return {
    channels,
    loading,
    ...rest,
  }
}
